import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { editSubscriptionPlan } from '../../apis/subscription-plans/edit-subscription-plan';
import { capitalize } from '../../utils/helpers';
import {
  IAddSubscriptionPlans,
  IGetAllPlantypesDocs,
  IGetSubscriptionPlans,
  ISubscriptionPlanColors,
} from '../../utils/types';
import Button from '../Button';
import Input from '../Input';
import Modal from '../Modal';
import Select from '../Select';
import Textarea from '../Textarea';
import { subscriptionPlanSchema } from './Schema';
import { SUBSCRIPTION_PLAN_CATEGORY } from '../../utils/Enums';

interface PropTypes {
  isVisible: boolean;
  onHide: () => void;
  onSuccess: () => void;
  planTypes?: IGetAllPlantypesDocs[];
  data: IGetSubscriptionPlans | undefined;
}

export const selectedColors = [
  ISubscriptionPlanColors.LIGHT_GREEN,
  ISubscriptionPlanColors.PURPLE,
  ISubscriptionPlanColors.RED,
  ISubscriptionPlanColors.PINK,
  ISubscriptionPlanColors.YELLOW,
  ISubscriptionPlanColors.BLUE,
  ISubscriptionPlanColors.DARK_GREEN,
  ISubscriptionPlanColors.ORANGE,
  ISubscriptionPlanColors.VIOLET,
  ISubscriptionPlanColors.DARK_PURPLE,
];

const EditSubscriptionPlanModule = ({
  isVisible,
  onHide,
  onSuccess,
  planTypes,
  data,
}: PropTypes) => {
  const [selectedColor, setSelectedColor] = useState<string>('');
  const handleHide = () => {
    onHide();
  };
  const isOnFreeTrial =
    data?.category === SUBSCRIPTION_PLAN_CATEGORY.BUSINESS_FREE_TRIAL;
  type FormValues = IAddSubscriptionPlans;

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      planType:
        planTypes?.find(
          (plan) =>
            plan.planType.toLowerCase().trim() ===
            data?.planType?.toLowerCase().trim(),
        )?._id ?? '',
      planName: data?.planName ?? '',
      description: data?.description ?? '',
      price: !isOnFreeTrial ? data?.price.toString() : undefined,
      durationDays: isOnFreeTrial ? data?.durationDays?.toString() : undefined,
      deliveries: data?.deliveries.toString() ?? '',
      colorCode: data?.colorCode ?? '',
    },
    validationSchema: subscriptionPlanSchema(isOnFreeTrial),
    onSubmit: async (values, { resetForm }) => {
      let payload;
      if (isOnFreeTrial) {
        const { price, ...rest } = values;
        payload = {
          ...rest,
          ...{ category: SUBSCRIPTION_PLAN_CATEGORY.BUSINESS_FREE_TRIAL },
        };
      } else {
        const { durationDays, ...rest } = values;
        payload = {
          ...rest,
          ...{ category: SUBSCRIPTION_PLAN_CATEGORY.BUSINESS_REGULAR },
        };
      }
      const res = await editSubscriptionPlan(data?._id!, payload);
      if (res.success) {
        toast('Subscription Plan Updated Successfully', { type: 'success' });
        resetForm();
        onSuccess();
        handleHide();
      } else {
        toast(res.error?.error, {
          type: 'error',
        });
      }
    },
  });

  const isEditButtonDisabled = !formik.dirty;
  useEffect(() => {
    if (data?.colorCode) {
      setSelectedColor(data.colorCode);
    }
  }, [data]);
  return (
    <Modal
      isVisible={isVisible}
      onHide={() => {
        formik.resetForm();
        setSelectedColor('');
        handleHide();
      }}
      containerClassName='lg:max-w-4xl'
      heading={
        isOnFreeTrial ? 'Edit free subscription plan' : 'Edit subscription plan'
      }
    >
      <form onSubmit={formik.handleSubmit} className='px-8'>
        <div className='lg:grid lg:col-span-4 lg:grid-cols-4 lg:gap-x-4 mt-4'>
          <Input
            placeholder='Enter plan name'
            isInvalid={Boolean(
              formik.touched.planName && formik.errors.planName,
            )}
            className='input-primary'
            containerClassName='col-span-1'
            label='Plan name *'
            name='planName'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.planName}
            value={formik.values.planName}
          />
          <Select
            placeholder='Select plan type'
            isInvalid={Boolean(
              formik.touched.planType && formik.errors.planType,
            )}
            className='select-primary text-[1rem] lg:text-xs'
            containerClassName='col-span-1'
            label='Plan type *'
            name='planType'
            value={formik.values.planType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.planType}
            options={
              planTypes?.map((plan) => ({
                value: plan._id,
                label: capitalize(plan.planType),
              })) || []
            }
          />
          <Input
            containerClassName='pb-0'
            rightContent={
              <div className='flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black'>
                <FaUser />
              </div>
            }
            placeholder='Enter total deliveries'
            type='number'
            isInvalid={Boolean(
              formik.touched.deliveries && formik.errors.deliveries,
            )}
            className='input-primary'
            label='Deliveries available *'
            name='deliveries'
            onChange={formik.handleChange}
            value={formik.values.deliveries}
            onBlur={formik.handleBlur}
            error={formik.errors.deliveries}
          />
          {isOnFreeTrial ? (
            <Input
              containerClassName='pb-0'
              placeholder='Enter number of days'
              type='number'
              className='input-primary'
              isInvalid={Boolean(
                formik.touched.durationDays && formik.errors.durationDays,
              )}
              label='Days'
              name='durationDays'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.durationDays}
              value={formik.values.durationDays}
            />
          ) : (
            <Input
              containerClassName='pb-0'
              rightContent={
                <div className='flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black'>
                  $
                </div>
              }
              placeholder='Enter price'
              type='number'
              className='input-primary'
              isInvalid={Boolean(formik.touched.price && formik.errors.price)}
              label='Price *'
              name='price'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.price}
              value={formik.values.price}
            />
          )}
        </div>

        <Textarea
          containerClassName='col-span-6 mt-4'
          placeholder=''
          className='textarea-primary'
          isInvalid={Boolean(
            formik.touched.description && formik.errors.description,
          )}
          label='Plan description *'
          name='description'
          onChange={(e) => formik.setFieldValue('description', e.target.value)}
          onBlur={formik.handleBlur}
          error={formik.errors.description}
          value={formik.values.description}
        />
        <div className='flex justify-center space-x-2 mt-2'>
          {selectedColors.map((color) => (
            <div
              key={color}
              className={`w-5 h-5 rounded-full cursor-pointer border-2 ${
                selectedColor === color ? 'outline ' : 'outline-none'
              }`}
              style={{
                backgroundColor: color,
                outlineColor: selectedColor === color ? color : '',
              }}
              onClick={() => {
                setSelectedColor(color);
                formik.setFieldValue('colorCode', color);
              }}
            />
          ))}
        </div>
        <div className='flex justify-center space-x-2 mt-2'>
          {formik.touched.colorCode && formik.errors.colorCode && (
            <div className='text-red-500 text-sm mt-1'>
              {formik.errors.colorCode}
            </div>
          )}
        </div>
        <div className='flex mt-4 gap-2 items-center justify-center'>
          <Button
            className='w-full md:w-32 min-h-0 h-10 md:h-10'
            isLoading={formik.isSubmitting}
            type='submit'
            disabled={isEditButtonDisabled}
          >
            Save
          </Button>
          <div className='w-2 h-2 rounded-full'></div>
        </div>
      </form>
    </Modal>
  );
};

export default EditSubscriptionPlanModule;
